'use client';

import { useCallback, useEffect, useRef } from 'react';
import { Ga4PageView } from '@models/ga4';
import getGa4UserProperty from '@tags/ga4/gaPageViewProperty';
import useGlobalState from '@hooks/useGlobalState';
import useGa4Event from '@hooks/useGa4Event';
import { gaVirtualPageView } from '@utils/ga4';
import { usePathname, useSearchParams } from 'next/navigation';
import { NewGnbInfo } from '@wconcept/wconcept-common/models/renewal/gnb';
import { useGaGnbInfo } from './useGaGnbInfo';

interface GaPageViewProps {
  pageTitle: string;
  params?: Record<string, string | undefined>;
  isInitial?: boolean;
  fireOnce?: boolean;
}

const getPathnameFromUrl = (url?: string) => {
  if (!url) return '';

  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0] || '';
  }
};

export function GaLog<T>({
  pageTitle,
  params,
  isInitial = true,
  fireOnce = false,
}: GaPageViewProps) {
  const { appInfo, domainGender } = useGlobalState<T>();
  const { getEpDevice, sendGa4BannerView, sendGa4SubGnbView } = useGa4Event();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasFiredRef = useRef(false);
  const getGnbInfo = useGaGnbInfo();

  const checkGnbAd = async () => {
    if (appInfo?.isNativeAppBoth) return;

    try {
      const gnbList = await getGnbInfo();
      const currentPathname = window.location.pathname;
      const gnb = gnbList?.find(
        item =>
          getPathnameFromUrl(item.landingUrl) === currentPathname ||
          item.subMenuList?.some(
            subMenu =>
              getPathnameFromUrl(subMenu.landingUrl) === currentPathname
          )
      );

      if (gnb?.adYn === 'Y' && !appInfo?.isNativeAppBoth) {
        sendGa4BannerView({
          adYn: gnb.adYn,
          text: gnb.menuTitle,
          type: 'GNB',
        });
      }
    } catch (error) {
      console.error('GNB 광고 체크 실패:', error);
    }
  };

  /**
   * 서브 GNB 랜딩 페이지 노출 시 subgnb_view 전송
   * - 화면 진입 후 최초 노출
   * - GNB 좌우 스와이프(pathname 변경)로 노출
   * - 뒤로가기 재노출(pathname 변경 또는 bfcache pageshow)
   * - 새로고침 재노출
   */
  const sendGa4SubGnbEvent = useCallback(
    (gnbList?: NewGnbInfo[]) => {
      // 네이티브 앱에서는 subgnb_view 미전송
      if (appInfo?.isNativeAppBoth) return;

      const currentPathname = window.location.pathname;
      const matchedSubGnb = gnbList
        ?.flatMap(item => item.subMenuList ?? [])
        .find(
          subMenu => getPathnameFromUrl(subMenu.landingUrl) === currentPathname
        );

      if (!matchedSubGnb) return;

      sendGa4SubGnbView({
        issueCd: params?.issueCd,
        issueNm: matchedSubGnb.menuTitle,
      });
    },
    [appInfo?.isNativeAppBoth, params?.issueCd, sendGa4SubGnbView]
  );

  const sendGa4SubGnbExposureEvent = useCallback(
    async (refresh = false) => {
      if (appInfo?.isNativeAppBoth) return;

      try {
        const gnbList = await getGnbInfo(refresh);
        sendGa4SubGnbEvent(gnbList);
      } catch (error) {
        console.error('서브 GNB 노출 GA4 체크 실패:', error);
      }
    },
    [appInfo?.isNativeAppBoth, getGnbInfo, sendGa4SubGnbEvent]
  );

  // assistData 등 불안정 deps로 콜백 참조가 바뀌어도 effect가 재실행되지 않도록 ref 유지
  const sendGa4SubGnbExposureEventRef = useRef(sendGa4SubGnbExposureEvent);
  sendGa4SubGnbExposureEventRef.current = sendGa4SubGnbExposureEvent;

  useEffect(() => {
    if (fireOnce && hasFiredRef.current) return;
    if (fireOnce) hasFiredRef.current = true;

    const init = async () => {
      const pageObj: Ga4PageView = {
        title: pageTitle,
        location: window.location.href,
        ep_visit_domainGender: params?.domainGender ?? domainGender,
        ep_visit_device: getEpDevice(),
        ep_page_profileCD: params?.profileSeqNo ?? undefined,
        ep_page_issueCD: params?.issueCd ?? undefined,
        ep_page_issueNM: params?.issueNm ?? undefined,
        ep_page_tagExclusive: params?.exclusive || undefined,
        ep_page_creatorYN: params?.creatorYN ?? undefined,
        ep_page_brandNM: params?.brandNM ?? undefined,
        ep_page_itemCD: params?.itemCD ?? undefined,
        ep_page_itemNM: params?.itemNM ?? undefined,
        ep_page_brandCD: params?.brandCD
          ? parseInt(params.brandCD, 10)
          : undefined,
        ep_page_searchKeyword: params?.searchKeyword ?? undefined,
        ep_page_searchType: params?.searchType ?? undefined,
        ep_page_itemList: params?.itemList ?? undefined,
      };
      const pageviewObj = await getGa4UserProperty(pageObj);
      gaVirtualPageView(!!appInfo?.isNativeAppBoth, pageviewObj, isInitial);

      checkGnbAd();
    };

    init();
  }, [searchParams]);

  // 서브 GNB 노출: 최초 진입 / GNB 스와이프·이동 / 새로고침(재마운트)
  // pathname 변경 시에만 전송 (콜백 identity 변경으로 인한 중복 전송 방지)
  useEffect(() => {
    sendGa4SubGnbExposureEventRef.current();
  }, [pathname]);

  // 서브 GNB 노출: bfcache 뒤로가기 재노출
  useEffect(() => {
    const handlePageshow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        sendGa4SubGnbExposureEventRef.current(true);
      }
    };

    window.addEventListener('pageshow', handlePageshow);
    return () => {
      window.removeEventListener('pageshow', handlePageshow);
    };
  }, []);

  return null;
}
